import { useState, useEffect, useRef, useCallback } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import PerformanceReviewService from "Services/EmployeeServices/PerformanceReviewSerivce";

const usePerformanceReview = (employeeId = null, employeeName = null) => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [skills, setSkills] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [isFirstReview, setIsFirstReview] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [skillsChecked, setSkillsChecked] = useState(false);
  const [error, setError] = useState(null);
  const [countSkills, setCountSkills] = useState(0);
  const [prevoiusReview, setPreviousReview] = useState(null);
  const [existingSkills, setExistingSkills] = useState([]);

  const navigate = useNavigate();

  const fetchEmployees = useCallback(async () => {
    const supervisorId = localStorage.getItem("personalId");
    if (!supervisorId) {
      message.error("Supervisor ID not found");
      return;
    }

    setLoading(true);
    try {
      const res = await PerformanceReviewService.getDirectReports(supervisorId);

      setEmployees(res.data);
    } catch (err) {
      console.error("Error fetching direct reports:", err);
      message.error("Failed to load direct report employees");
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSkillsChecked(false);
  }, [employeeId]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  // Check if employee has skills
  const checkExistingSkills = useCallback(async (empId) => {
    if (!empId) return { hasSkills: false, count: 0 };

    try {
      const data = await PerformanceReviewService.getSkillsByEmployee(empId);
      const skillData = Array.isArray(data) ? data : [];

      setCountSkills(skillData.length);
      setExistingSkills(skillData);

      return {
        hasSkills: skillData.length > 0,
        count: skillData.length,
      };
    } catch (err) {
      console.error("Error checking skills:", err);
      setExistingSkills([]);
      return { hasSkills: false, count: 0 };
    }
  }, []);

  // Initialize employee and performance review
  useEffect(() => {
    if (!employeeId || skillsChecked) return;

    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        let data;
        try {
          data = await PerformanceReviewService.getPerformanceReviews(employeeId);
        } catch (err) {
          console.error("Error fetching reviews:", err);
        }

        if (!data) return;

        const emp = {
          employeeId: data.employeeId,
          firstName: data.firstName,
          lastName: data.lastName,
          departmentName: data.departmentName,
          departmentId: data.departmentId,
          supervisorFirstName: data.supervisorFirstName,
          supervisorLastName: data.supervisorLastName,
          hireDate: null,
          isReviewAllowed: data.isReviewAllowed,
          nextEligibleReviewDate: data.nextEligibleReviewDate,
        };
        setEmployee(emp);
        setReviews(data.performanceReviews || []);
        setIsFirstReview(!data.performanceReviews?.length);
        setIsButtonDisabled(!data.isReviewAllowed);

        // Check skills only once

        const { count } = await checkExistingSkills(emp.employeeId);

        setSkillsChecked(true);

        // Only show modal if:
        // - No previous reviews
        // - Skills are less than 5
        const shouldShowModal = !data.performanceReviews?.length && count < 5;

        setModalVisible(shouldShowModal);
      } catch (err) {
        console.error("Initialization error:", err);
        setError("Failed to initialize review data");
        message.error("Failed to initialize review data");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [employeeId, skillsChecked, checkExistingSkills, employeeName]);

  // Fetch available skills
  const fetchAvailableSkills = useCallback(async (departmentId) => {
    try {
      const data = await PerformanceReviewService.getAvailableSkills(departmentId);

      // If API returns empty or invalid data
      if (!Array.isArray(data) || data.length === 0) {
        setSkills([]);
        return [];
      }

      setSkills(data);
      return data;
    } catch (err) {
      console.warn("No predefined skills found, allowing manual entry");

      //  IMPORTANT: Do NOT show error
      setSkills([]);

      // Optional: show info once
      message.info("No predefined skills found. You can add custom skills.");

      return [];
    }
  }, []);

  const addSkills = useCallback(
    async (payload) => {
      try {
        // Ensure payload matches API structure
        const apiPayload = {
          employeeId: payload.employeeId,
          skillIds: payload.skillIds || [],
          customSkills: payload.customSkills || [],
        };

        const response = await PerformanceReviewService.addSkills(apiPayload);

        message.success("Skills added successfully!");
        setModalVisible(false);

        // Update the existing skills count
        setCountSkills(
          (prevCount) => prevCount + payload.skillIds.length + payload.customSkills.length
        );

        navigate("/employeedashboard/performance-review/form", {
          state: { employeeId: payload.employeeId, employeeName: employee?.employeeName },
        });
      } catch (err) {
        console.error("Error adding skills:", err);
        // Log more details about the error
        if (err.response) {
          console.error("Error response data:", err.response.data);
          console.error("Error response status:", err.response.status);
          console.error("Error response headers:", err.response.headers);
        }
        if (err.response?.headers?.details) {
          message.error(err.response.headers.details);
        } else if (err.response?.data?.message) {
          message.error(err.response.data.message);
        } else if (err.response?.data) {
          // Try to extract a meaningful error message from the response data
          const errorMessage =
            typeof err.response.data === "string"
              ? err.response.data
              : JSON.stringify(err.response.data);
          message.error(`Failed to add skills: ${errorMessage}`);
        } else {
          message.error("Failed to add skills. Please check the console for details.");
        }
        throw err;
      }
    },
    [employee, navigate]
  );

  const navigateToReviewForm = useCallback(() => {
    navigate("/employeedashboard/performance-review", {
      state: {
        employeeId: employee?.employeeId || employeeId,
        employeeName: employee?.employeeName || employeeName,
      },
    });
  }, [employee, employeeId, employeeName, navigate]);

  const submitPerformanceReview = useCallback(async (payload) => {
    try {
      setLoading(true);

      // Call your performance review submission API
      const response = await PerformanceReviewService.submitPerformanceReview(payload);

      // Check if the response indicates success
      if (response) {
        message.success("Performance review submitted successfully!");
        return response;
      }
    } catch (err) {
      console.error("❌ Error submitting performance review:", err);

      // Only show error if it's a genuine error
      if (err.response && err.response.status >= 400) {
        message.error("Failed to submit performance review");
        throw err;
      } else {
        // If it's a network error or other issue, still throw
        throw err;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreviousReview = useCallback(async (empId) => {
    try {
      setLoading(true);
      setError(null);

      if (!empId) {
        setError("Employee ID is required");
        return null;
      }

      const data = await PerformanceReviewService.getPreviousReview(empId);

      if (data?.performanceReview) {
        setPreviousReview(data.performanceReview);
        return data.performanceReview;
      } else {
        setError("No evaluation data found in response");
        return null;
      }
    } catch (err) {
      console.error("Fetch error:", err);
      const errorMsg = err.message || "Failed to fetch performance review data";
      setError(errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    fetchPreviousReview,
    employees,
    loading,
    error,
    fetchEmployees,
    employee,
    reviews,
    skills,
    selectedSkills,
    setSelectedSkills,
    isFirstReview,
    isButtonDisabled,
    modalVisible,
    setModalVisible,
    fetchAvailableSkills,
    addSkills,
    navigateToReviewForm,
    submitPerformanceReview,
    countSkills,
    prevoiusReview,
    existingSkills,
  };
};

export default usePerformanceReview;
